import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import classNames from "classnames";

import StackedColumnChart from "./StackedColumnChart";
import { getChartsData as onGetChartsData } from "../../store/actions";

import WelcomeComp from "./WelcomeComp";
import MonthlyEarning from "./MonthlyEarning";
import ActivityComp from "./ActivityComp";
import TopCities from "./TopCities";
import LatestTransaction from "./LatestTranaction";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

// ─── Müşteri Hızlı Eylem Kartları ───────────────────────────────────────────
const CustomerQuickActions = () => (
  <Row className="mb-4">
    {[
      { icon: "bx-plus-circle", color: "primary",  label: "Yeni Görev Talebi",   sub: "Yük taşıma talebi oluştur", to: "/ecommerce-add-product" },
      { icon: "bx-list-ul",     color: "info",     label: "Taleplerim",           sub: "Mevcut taleplerinizi görün",  to: "/job-apply" },
      { icon: "bx-credit-card", color: "warning",  label: "Ceza Ödemelerim",      sub: "Ödenmemiş borçlarınız",       to: "/borc-yonetimi" },
      { icon: "bx-user",        color: "success",  label: "Hesabım",              sub: "Profil bilgileriniz",          to: "/hesabim" },
    ].map((item, i) => (
      <Col md="3" sm="6" key={i} className="mb-3">
        <Link to={item.to} className="text-decoration-none">
          <Card className={`border-0 shadow-sm h-100 border-start border-4 border-${item.color}`} style={{ transition: "transform .15s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            <CardBody className="d-flex align-items-center gap-3 py-3">
              <div className={`avatar-sm rounded-circle bg-${item.color} bg-opacity-10 d-flex align-items-center justify-content-center`}>
                <i className={`bx ${item.icon} font-size-22 text-${item.color}`}></i>
              </div>
              <div>
                <h6 className="mb-0 fw-bold">{item.label}</h6>
                <small className="text-muted">{item.sub}</small>
              </div>
            </CardBody>
          </Card>
        </Link>
      </Col>
    ))}
  </Row>
);

// ─── Sürücü Hızlı Eylem Kartları ─────────────────────────────────────────────
const DriverQuickActions = () => (
  <Row className="mb-4">
    {[
      { icon: "bx-search-alt",  color: "primary",  label: "Uygun İşler",     sub: "Yeni taşıma ilanlarını gör",  to: "/job-list" },
      { icon: "bx-briefcase",   color: "success",  label: "Görevlerim",      sub: "Kabul ettiğin görevler",       to: "/job-apply" },
      { icon: "bx-calendar",    color: "info",     label: "Müsaitliğim",     sub: "Takviminizi düzenleyin",       to: "/calendar" },
      { icon: "bx-user",        color: "warning",  label: "Hesabım",         sub: "Profil bilgileriniz",          to: "/hesabim" },
    ].map((item, i) => (
      <Col md="3" sm="6" key={i} className="mb-3">
        <Link to={item.to} className="text-decoration-none">
          <Card className={`border-0 shadow-sm h-100 border-start border-4 border-${item.color}`} style={{ transition: "transform .15s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            <CardBody className="d-flex align-items-center gap-3 py-3">
              <div className={`avatar-sm rounded-circle bg-${item.color} bg-opacity-10 d-flex align-items-center justify-content-center`}>
                <i className={`bx ${item.icon} font-size-22 text-${item.color}`}></i>
              </div>
              <div>
                <h6 className="mb-0 fw-bold">{item.label}</h6>
                <small className="text-muted">{item.sub}</small>
              </div>
            </CardBody>
          </Card>
        </Link>
      </Col>
    ))}
  </Row>
);

// ─── Ana Dashboard ────────────────────────────────────────────────────────────
const Dashboard = props => {
  document.title = "Anasayfa | Lojgo";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const userId = authUser?.ID;
  const userTypeId = Number(authUser?.Users_Type_ID);
  const isAdmin    = userTypeId === 3;
  const isDriver   = userTypeId === 2 || userTypeId === 4;
  const isCustomer = userTypeId === 1;

  const DashboardProperties = createSelector(
    (state) => state.Dashboard,
    (dashboard) => ({ chartsData: dashboard.chartsData })
  );
  const { chartsData } = useSelector(DashboardProperties);

  const [stats, setStats] = useState({ cards: [], isLoading: true });
  const [periodData, setPeriodData] = useState([]);
  const [periodType, setPeriodType] = useState("yearly");

  const dispatch = useDispatch();

  useEffect(() => { setPeriodData(chartsData); }, [chartsData]);

  useEffect(() => {
    if (isAdmin) dispatch(onGetChartsData("yearly"));
  }, [dispatch, isAdmin]);

  const onChangeChartPeriod = pType => {
    setPeriodType(pType);
    dispatch(onGetChartsData(pType));
  };

  // İstatistikleri role göre çek
  useEffect(() => {
    if (!userId) return;
    const fetch3 = async () => {
      try {
        let cards = [];

        if (isAdmin) {
          const [r1, r2, r3, r4] = await Promise.all([
            fetch(`${BASE_URL}/Advert?$filter=Is_Active eq true`),
            fetch(`${BASE_URL}/Advert`),
            fetch(`${BASE_URL}/Advert_DeliveryStatus?$filter=Advert_DeliveryStatus_Status eq 'Tamamlandı'`),
            fetch(`${BASE_URL}/Advert_Offers?$filter=Offer_Status eq 'Bekliyor'`),
          ]);
          const [d1, d2, d3, d4] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json()]);
          const totalCiro = (d2.value || []).reduce((s, a) => s + (a.Price || 0), 0);
          cards = [
            { title: "Aktif İlanlar",         icon: "bx-car",          color: "primary", value: d1.value?.length || 0 },
            { title: "Tamamlanan Taşımalar",   icon: "bx-check-circle", color: "success", value: d3.value?.length || 0 },
            { title: "Bekleyen Teklifler",     icon: "bx-time-five",    color: "warning", value: d4.value?.length || 0 },
            { title: "Toplam Ciro",            icon: "bx-money",        color: "info",    value: `${totalCiro.toLocaleString("tr-TR")} TL` },
          ];

        } else if (isDriver) {
          const [r1, r2, r3] = await Promise.all([
            fetch(`${BASE_URL}/Advert?$filter=Is_Active eq true and Is_AdminCheck eq true`),
            fetch(`${BASE_URL}/Advert_Offers?$filter=Users_ID eq ${userId} and Offer_Status eq 'Kabul Edildi'`),
            fetch(`${BASE_URL}/Advert_Offers?$filter=Users_ID eq ${userId} and Offer_Status eq 'Bekliyor'`),
          ]);
          const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
          cards = [
            { title: "Uygun İşler",        icon: "bx-search-alt",   color: "primary", value: d1.value?.length || 0 },
            { title: "Kabul Edilen Görev", icon: "bx-check-circle", color: "success", value: d2.value?.length || 0 },
            { title: "Bekleyen Teklifim",  icon: "bx-time-five",    color: "warning", value: d3.value?.length || 0 },
          ];

        } else {
          // Müşteri
          const [r1, r2, r3] = await Promise.all([
            fetch(`${BASE_URL}/Advert?$filter=Users_ID eq ${userId} and Is_Active eq true`),
            fetch(`${BASE_URL}/Advert?$filter=Users_ID eq ${userId}`),
            fetch(`${BASE_URL}/Users_Advert_Pricing?$filter=Users_ID eq ${userId} and Is_Paid eq false`),
          ]);
          const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
          const totalSpent = (d2.value || []).reduce((s, a) => s + (a.Price || 0), 0);
          cards = [
            { title: "Aktif Talebim",        icon: "bx-list-ul",      color: "primary", value: d1.value?.length || 0 },
            { title: "Toplam Harcamam",       icon: "bx-money",        color: "success", value: `${totalSpent.toLocaleString("tr-TR")} TL` },
            { title: "Ödenmemiş Borcum",      icon: "bx-credit-card",  color: "danger",  value: d3.value?.length || 0 },
          ];
        }

        setStats({ cards, isLoading: false });
      } catch (e) {
        console.error("Dashboard stats error:", e);
        setStats(prev => ({ ...prev, isLoading: false }));
      }
    };
    fetch3();
  }, [userId, isAdmin, isDriver, isCustomer]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Anasayfa")} breadcrumbItem={props.t("Özet Ekranı")} />

          {/* ─── Üst satır: Hoş geldin + istatistikler ─── */}
          <Row>
            <Col xl="4">
              <WelcomeComp />
              {isAdmin && <MonthlyEarning />}
            </Col>

            <Col xl="8">
              {/* Mini istatistik kartları */}
              <Row>
                {stats.isLoading
                  ? [1, 2, 3].map(k => (
                      <Col md="4" key={k}>
                        <Card className="mini-stats-wid">
                          <CardBody className="text-center py-4">
                            <Spinner size="sm" color="primary" />
                          </CardBody>
                        </Card>
                      </Col>
                    ))
                  : stats.cards.map((card, i) => (
                      <Col md={isAdmin ? "3" : "4"} key={i}>
                        <Card className="mini-stats-wid">
                          <CardBody>
                            <div className="d-flex">
                              <div className="flex-grow-1">
                                <p className="text-muted fw-medium mb-2">{card.title}</p>
                                <h4 className="mb-0">{card.value}</h4>
                              </div>
                              <div className={`avatar-sm rounded-circle bg-${card.color} align-self-center mini-stat-icon`}>
                                <span className={`avatar-title rounded-circle bg-${card.color}`}>
                                  <i className={`bx ${card.icon} font-size-24`}></i>
                                </span>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
              </Row>

              {/* Admin: grafik — Diğerleri: hızlı eylem kartları */}
              {isAdmin ? (
                <Card>
                  <CardBody>
                    <div className="d-sm-flex flex-wrap">
                      <h4 className="card-title mb-4">Taşıma İstatistikleri</h4>
                      <div className="ms-auto">
                        <ul className="nav nav-pills">
                          {[
                            { key: "weekly",  label: "Hafta" },
                            { key: "monthly", label: "Ay" },
                            { key: "yearly",  label: "Yıl" },
                          ].map(p => (
                            <li className="nav-item" key={p.key}>
                              <Link to="#"
                                className={classNames({ active: periodType === p.key }, "nav-link")}
                                onClick={() => onChangeChartPeriod(p.key)}>
                                {p.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <StackedColumnChart periodData={periodData} dataColors='["--bs-primary", "--bs-warning", "--bs-success"]' />
                  </CardBody>
                </Card>
              ) : isDriver ? (
                <DriverQuickActions />
              ) : (
                <CustomerQuickActions />
              )}
            </Col>
          </Row>

          {/* ─── Alt satır: Admin'e özgü bölümler ─── */}
          {isAdmin && (
            <>
              <Row>
                <Col xl="6"><ActivityComp /></Col>
                <Col xl="6"><TopCities /></Col>
              </Row>
            </>
          )}

          {/* Son ödemeler: herkes görür */}
          <Row>
            <Col lg="12">
              <LatestTransaction />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
};

export default withTranslation()(Dashboard);
